// ============================================================================
// 旧 Firebase httpsCallable のラッパ群を、Phase 3 で立てた HTTP API 用に置換
// ============================================================================
// 全エンドポイントは Cognito Guardian Pool の id token (JWT) が必要。
// ルートの正は chloe-infra/live/dev/api-gateway.tf の local.api_routes。
// 旧名 → 新ルートの対応：
//   pairGuardian                 → POST /me/devices
//   updateGuardianFcmToken       → PUT  /me/fcm-token
//   getNotificationSettings      → GET  /me/notification-settings
//   updateNotificationSettings   → PUT  /me/notification-settings
//   getNotificationHistory       → GET  /me/notifications?limit=N
//   acknowledgeNotification      → POST /me/notifications/ack
//   sendGuardianMessage          → POST /devices/{deviceId}/messages
//   getGuardianMessages          → GET  /devices/{deviceId}/messages?limit=N   ← device-scoped
//   updateGuardianDisplayName    → PUT  /me/profile
//   getGuardianProfile           → GET  /me/profile
//   getDeviceProfile             → GET  /devices/{deviceId}/profile
//   updateDeviceProfile          → PUT  /devices/{deviceId}/profile
// ----------------------------------------------------------------------------

import { api } from "./api";

// ----------------------------------------------------------------------------
// Pairing
// ----------------------------------------------------------------------------

export interface PairGuardianResult {
  success: boolean;
  message: string;
}

export async function pairGuardian(
  code: string,
  fcmToken: string | null = null,
): Promise<PairGuardianResult> {
  // Lambda 側は code のみ受け取る。fcmToken は後段で /me/fcm-token で更新可能なため optional。
  const body: { code: string; fcmToken?: string } = { code };
  if (fcmToken) body.fcmToken = fcmToken;
  return api.post<PairGuardianResult>("/me/devices", body);
}

// ----------------------------------------------------------------------------
// FCM token
// ----------------------------------------------------------------------------

export interface UpdateFcmTokenResult {
  success: boolean;
}

export async function updateFcmToken(fcmToken: string): Promise<UpdateFcmTokenResult> {
  return api.put<UpdateFcmTokenResult>("/me/fcm-token", { fcmToken });
}

// ----------------------------------------------------------------------------
// Notification settings
// ----------------------------------------------------------------------------

export interface DeviceNotificationSettings {
  // Phase 4c で旧 linkId は削除（deviceId で device_guardians 行を特定する）
  deviceId: string;
  deviceName: string;
  notifyHazard: boolean;
  notifyEmergency: boolean;
  notifyDaily: boolean;
}

interface GetNotificationSettingsResult {
  devices: DeviceNotificationSettings[];
}

export async function getNotificationSettings(): Promise<DeviceNotificationSettings[]> {
  const res = await api.get<GetNotificationSettingsResult>("/me/notification-settings");
  return res.devices;
}

export interface UpdateNotificationSettingsResult {
  success: boolean;
}

export async function updateNotificationSettings(
  deviceId: string,
  settings: {
    notifyHazard: boolean;
    notifyEmergency: boolean;
    notifyDaily: boolean;
  }
): Promise<UpdateNotificationSettingsResult> {
  // Lambda 側は deviceId をキーにして device_guardians 行を特定する。
  // 旧 Firestore 時代の linkId は廃止。
  return api.put<UpdateNotificationSettingsResult>("/me/notification-settings", {
    deviceId,
    ...settings,
  });
}

// ----------------------------------------------------------------------------
// Notification history
// ----------------------------------------------------------------------------

// createdAt / acknowledgedAt は Lambda から ISO 文字列で返る。
// 旧来 number 宣言だったが runtime 実体に合わせて string に修正済。
export interface NotificationHistoryItem {
  id: string;
  deviceId: string;
  title: string;
  body: string;
  category: string;
  createdAt: string;
  acknowledgedAt: string | null;
  videoUrl?: string | null;
}

interface GetNotificationHistoryResult {
  notifications: NotificationHistoryItem[];
  nextCursor: string | null;
}

/** 単一ページ取得用の戻り値（無限スクロール用） */
export interface NotificationHistoryPage {
  items: NotificationHistoryItem[];
  nextCursor: string | null;
}

/**
 * 通知履歴を 1 ページ取得する。`before` を渡すとその SK より古い側を取得。
 * 詳細仕様は docs/message-pagination.md。
 */
export async function fetchNotificationHistoryPage(
  limit?: number,
  before?: string | null
): Promise<NotificationHistoryPage> {
  const res = await api.get<GetNotificationHistoryResult>("/me/notifications", {
    query: { limit, ...(before ? { before } : {}) },
  });
  return { items: res.notifications, nextCursor: res.nextCursor ?? null };
}

/**
 * 後方互換用ラッパ。最新ページのアイテムだけ返す。
 * 新規コードは fetchNotificationHistoryPage を使うこと。
 */
export async function getNotificationHistory(limit?: number): Promise<NotificationHistoryItem[]> {
  const page = await fetchNotificationHistoryPage(limit);
  return page.items;
}

export async function acknowledgeNotification(notificationId: string): Promise<{ success: boolean }> {
  return api.post<{ success: boolean }>("/me/notifications/ack", { notificationId });
}

// ----------------------------------------------------------------------------
// Guardian messages（device-scoped に変わった点に注意）
// ----------------------------------------------------------------------------

export interface GuardianMessage {
  id: string;
  deviceId: string;
  message: string;
  status: string;
  urgent?: boolean;
  createdAt: string;
  deliveredAt: string | null;
}

interface SendGuardianMessageResult {
  success: boolean;
  messageId: string;
}

interface GetGuardianMessagesResult {
  messages: GuardianMessage[];
  nextCursor: string | null;
}

/** 単一 device 1 ページ取得 */
export interface GuardianMessagesPage {
  items: GuardianMessage[];
  nextCursor: string | null;
}

export async function sendGuardianMessage(
  deviceId: string,
  message: string,
  urgent?: boolean
): Promise<SendGuardianMessageResult> {
  return api.post<SendGuardianMessageResult>(`/devices/${encodeURIComponent(deviceId)}/messages`, {
    message,
    urgent,
  });
}

/**
 * device-scoped で 1 ページ取得。`before` を渡すと古い側へ進む。
 */
export async function fetchGuardianMessagesPage(
  deviceId: string,
  limit?: number,
  before?: string | null
): Promise<GuardianMessagesPage> {
  const res = await api.get<GetGuardianMessagesResult>(
    `/devices/${encodeURIComponent(deviceId)}/messages`,
    { query: { limit, ...(before ? { before } : {}) } }
  );
  return { items: res.messages, nextCursor: res.nextCursor ?? null };
}

/** 後方互換ラッパ */
export async function getGuardianMessages(
  deviceId: string,
  limit?: number
): Promise<GuardianMessage[]> {
  const page = await fetchGuardianMessagesPage(deviceId, limit);
  return page.items;
}

/**
 * 複数 device の最新ページを並列取得して結合する（初期表示用）。
 * 各 device の nextCursor も Record で返す（hasMore 判定 + ページング継続用）。
 */
export interface AllGuardianMessagesPage {
  items: GuardianMessage[];
  /** deviceId → 次に渡すべき cursor（null は枯れ） */
  nextCursors: Record<string, string | null>;
}

export async function fetchAllGuardianMessagesPage(
  deviceIds: string[],
  limit?: number
): Promise<AllGuardianMessagesPage> {
  if (deviceIds.length === 0) {
    return { items: [], nextCursors: {} };
  }
  const pages = await Promise.all(
    deviceIds.map(async (id) => {
      try {
        const page = await fetchGuardianMessagesPage(id, limit);
        return { deviceId: id, page };
      } catch {
        return {
          deviceId: id,
          page: { items: [] as GuardianMessage[], nextCursor: null },
        };
      }
    })
  );
  const items: GuardianMessage[] = [];
  const nextCursors: Record<string, string | null> = {};
  for (const { deviceId, page } of pages) {
    items.push(...page.items);
    nextCursors[deviceId] = page.nextCursor;
  }
  return { items, nextCursors };
}

/** 後方互換ラッパ（最新ページの items だけ返す） */
export async function getAllGuardianMessages(
  deviceIds: string[],
  limit?: number
): Promise<GuardianMessage[]> {
  const { items } = await fetchAllGuardianMessagesPage(deviceIds, limit);
  return items;
}

// ----------------------------------------------------------------------------
// Guardian profile
// ----------------------------------------------------------------------------

export async function updateGuardianDisplayName(
  displayName: string,
  phoneNumber?: string
): Promise<{ success: boolean }> {
  return api.put<{ success: boolean }>("/me/profile", { displayName, phoneNumber });
}

export async function getGuardianProfile(): Promise<{ displayName: string; phoneNumber: string }> {
  return api.get<{ displayName: string; phoneNumber: string }>("/me/profile");
}

// ----------------------------------------------------------------------------
// Device profile
// ----------------------------------------------------------------------------
//
// PWA の UI は旧 Firestore 時代のフィールド名（profileName / profileGender /
// profileBirthday / emergencyPhoneNumber）を保持している。
// AWS Lambda 側は新しい Aurora カラムベースの名前（profileName / gender /
// birthday / homeLat / homeLng）を期待する。
// ここで PWA ↔ Lambda 間のマッピングを吸収する。

export interface DeviceProfile {
  profileName: string;
  profileGender: string;
  profileBirthday: string;
  emergencyPhoneNumber: string;
}

interface LambdaDeviceProfile {
  id?: string;
  userName?: string | null;
  profileName?: string | null;
  birthday?: string | null;
  gender?: string | null;
  homeLat?: number | null;
  homeLng?: number | null;
  emergencyPhoneNumber?: string | null;
}

function fromLambda(p: LambdaDeviceProfile): DeviceProfile {
  return {
    profileName: p.profileName ?? p.userName ?? "",
    profileGender: p.gender ?? "",
    profileBirthday: p.birthday ?? "",
    emergencyPhoneNumber: p.emergencyPhoneNumber ?? "",
  };
}

function toLambda(p: DeviceProfile): Record<string, string | null> {
  return {
    profileName: p.profileName,
    gender: p.profileGender || null,
    birthday: p.profileBirthday || null,
    emergencyPhoneNumber: p.emergencyPhoneNumber || null,
  };
}

export async function getDeviceProfile(deviceId: string): Promise<DeviceProfile> {
  const lambda = await api.get<LambdaDeviceProfile>(
    `/devices/${encodeURIComponent(deviceId)}/profile`
  );
  return fromLambda(lambda);
}

export async function updateDeviceProfile(
  deviceId: string,
  profile: DeviceProfile
): Promise<{ success: boolean }> {
  await api.put<LambdaDeviceProfile>(
    `/devices/${encodeURIComponent(deviceId)}/profile`,
    toLambda(profile)
  );
  return { success: true };
}
